import { useState } from "react";
import { useAuth } from "../contexts/authProvider";
import { acceptLegalDocuments } from "services/userService";
import { LEGAL_DOCUMENTS } from "utils/legalDocs";
import { useModal } from "hooks/useModal";
import Icon from "./AppIcon";

/**
 * Gate de re-aceite dos documentos legais.
 *
 * Aparece quando os Termos ou a Política mudaram e o usuário ainda não aceitou
 * a versão vigente. É bloqueante de propósito: enquanto o aceite não é
 * registrado, a versão nova não é oponível a esta pessoa — sancioná-la com base
 * numa cláusula que ela nunca viu é o cenário que este gate existe para evitar.
 *
 * Cada documento tem sua própria caixa. Aceitar em bloco, sem discriminar o que
 * está sendo aceito, enfraquece o próprio aceite.
 */
const LegalReacceptanceModal = () => {
  useModal(true, () => {}, { closeOnEscape: false, closeOnBack: false });

  const { pendingLegalDocuments, refreshLegalStatus, logout } = useAuth();
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  const pending = pendingLegalDocuments || [];
  const allChecked = pending.every((key) => checked[key]);

  const toggle = (key) =>
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleAccept = async () => {
    if (!allChecked || saving) return;
    setSaving(true);
    setError(null);
    try {
      const versions = pending.reduce((acc, key) => {
        acc[key] = LEGAL_DOCUMENTS[key]?.version;
        return acc;
      }, {});

      await acceptLegalDocuments(pending, versions);
      await refreshLegalStatus();
    } catch {
      // Sem aceite registrado o gate continua. Liberar a tela aqui daria a
      // impressão de aceite provado quando nada foi gravado.
      setError(
        "Não foi possível registrar seu aceite. Verifique sua conexão e tente novamente.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-6 pb-4 text-center">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FileText" size={28} className="text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">
            Atualizamos nossos documentos
          </h2>
          <p className="text-sm text-muted-foreground">
            {pending.length > 1
              ? "Os Termos de Uso e a Política de Privacidade foram atualizados."
              : `${LEGAL_DOCUMENTS[pending[0]]?.label || "Um documento"} foi atualizado.`}{" "}
            Leia e confirme para continuar usando a plataforma.
          </p>
        </div>

        <div className="px-6 space-y-3 overflow-y-auto">
          {pending.map((key) => {
            const doc = LEGAL_DOCUMENTS[key];
            if (!doc) return null;
            return (
              <label
                key={key}
                className="flex items-start gap-3 rounded-xl border border-border p-4 cursor-pointer hover:bg-muted/40 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={Boolean(checked[key])}
                  onChange={() => toggle(key)}
                  className="mt-1 h-4 w-4 flex-shrink-0 accent-primary"
                />
                <span className="text-sm text-foreground">
                  Li e concordo com{" "}
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="text-primary font-medium hover:underline"
                  >
                    {doc.label}
                  </a>
                  <span className="block text-xs text-muted-foreground mt-1">
                    Versão de {formatVersion(doc.version)}
                  </span>
                </span>
              </label>
            );
          })}

          <div className="flex items-start gap-2 rounded-lg bg-muted/50 border border-border p-3">
            <Icon
              name="Info"
              size={16}
              className="text-muted-foreground flex-shrink-0 mt-0.5"
            />
            <p className="text-xs text-muted-foreground">
              Registramos a data, o horário e a versão que você está aceitando.
              Você pode consultar os documentos completos a qualquer momento
              pelos links acima.
            </p>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-3 flex items-start gap-2 p-3 rounded-lg border bg-destructive/10 border-destructive/20">
            <Icon
              name="AlertCircle"
              size={16}
              className="text-destructive flex-shrink-0 mt-0.5"
            />
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <div className="px-6 py-6 space-y-2">
          <button
            onClick={handleAccept}
            disabled={!allChecked || saving}
            className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-xl font-semibold transition-all hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Icon name="Loader2" size={18} className="animate-spin" />
                Registrando...
              </>
            ) : (
              "Aceitar e continuar"
            )}
          </button>

          {confirmingLogout ? (
            <div className="flex gap-2">
              <button
                onClick={() => setConfirmingLogout(false)}
                className="flex-1 px-4 py-2 text-sm rounded-xl border border-border text-foreground hover:bg-muted transition-colors"
              >
                Voltar
              </button>
              <button
                onClick={logout}
                className="flex-1 px-4 py-2 text-sm rounded-xl border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
              >
                Sair mesmo assim
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingLogout(true)}
              className="w-full px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Não concordo, sair da conta
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** "2026-08-11" → "11/08/2026". */
function formatVersion(version) {
  if (typeof version !== "string") return "—";
  const [year, month, day] = version.split("-");
  return day && month && year ? `${day}/${month}/${year}` : version;
}

export default LegalReacceptanceModal;
